// Campaigns.js
import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../AuthProvider";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  FaPlay,
  FaPause,
  FaEdit,
  FaTrash,
  FaSearch,
  FaFilter,
  FaPlus,
  FaEye,
  FaCopy,
} from "react-icons/fa";
import { toast } from "react-toastify";

/**
 * Campaigns Component
 *
 * This component displays a tabular list of all campaigns.
 * It shares the same data source as the CampaignBuilder component
 * and provides links to create and edit campaigns using the CampaignBuilder.
 *
 * The navigation links ensure consistency between the sidebar navigation
 * and the campaign list navigation.
 */
const Campaigns = () => {
  const { user } = useContext(AuthContext);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [activeTab, setActiveTab] = useState("all");
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Fix the collection path - add "collections" segment to make path have odd number of segments
        let campaignsQuery = collection(
          db,
          "userContent",
          user.uid,
          "aiSdr",
          "collections", // Add this segment to make path have odd number of segments
          "campaigns"
        );

        if (filterStatus !== "all") {
          campaignsQuery = query(
            campaignsQuery,
            where("status", "==", filterStatus),
            orderBy("updatedAt", "desc")
          );
        } else {
          campaignsQuery = query(campaignsQuery, orderBy("updatedAt", "desc"));
        }

        const campaignsSnapshot = await getDocs(campaignsQuery);

        const campaignsList = campaignsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        }));

        setCampaigns(campaignsList);
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        toast.error("Failed to load campaigns");
        setError("Error loading campaigns. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [user, filterStatus]);

  const toggleCampaignStatus = async (campaign) => {
    if (!user) return;

    const newStatus = campaign.status === "active" ? "paused" : "active";

    try {
      // Fix the document path by adding "collections" segment
      const campaignRef = doc(
        db,
        "userContent",
        user.uid,
        "aiSdr",
        "collections", // Add this segment to make path structure consistent
        "campaigns",
        campaign.id
      );
      await updateDoc(campaignRef, {
        status: newStatus,
        updatedAt: new Date(),
      });

      setCampaigns((prev) =>
        prev.map((c) =>
          c.id === campaign.id ? { ...c, status: newStatus } : c
        )
      );

      toast.success(
        `Campaign ${newStatus === "active" ? "activated" : "paused"}`
      );
    } catch (error) {
      console.error("Error updating campaign status:", error);
      toast.error("Failed to update campaign status");
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this campaign? This action cannot be undone."
      )
    ) {
      return;
    }

    if (!user) return;

    try {
      // Fix the document path by adding "collections" segment
      const campaignRef = doc(
        db,
        "userContent",
        user.uid,
        "aiSdr",
        "collections", // Add this segment to make path structure consistent
        "campaigns",
        campaignId
      );
      await deleteDoc(campaignRef);

      setCampaigns((prev) => prev.filter((c) => c.id !== campaignId));
      toast.success("Campaign deleted");
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error("Failed to delete campaign");
    }
  };

  // Filter campaigns based on search term
  const filteredCampaigns = campaigns.filter((campaign) =>
    campaign.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "paused":
        return "bg-yellow-100 text-yellow-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "paused":
        return "bg-yellow-100 text-yellow-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString();
  };

  const handleViewCampaign = (ownerUid, campaignId) => {
    // Implementation of handleViewCampaign
  };

  const handleDuplicateCampaign = (campaign) => {
    // Implementation of handleDuplicateCampaign
  };

  const handleDeleteCampaign = (campaignId) => {
    // Implementation of handleDeleteCampaign
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex-1 p-6">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">Campaigns</h1>
          <Link
            to="/ai-sdr/campaign-builder"
            className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 flex items-center"
          >
            <FaPlus className="mr-2" /> New Campaign
          </Link>
        </div>

        {/* Tab navigation */}
        <div className="border-b mb-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab("all")}
              className={`pb-4 px-1 font-medium ${
                activeTab === "all"
                  ? "border-b-2 border-purple-600 text-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              All Campaigns
            </button>
            <button
              onClick={() => setActiveTab("active")}
              className={`pb-4 px-1 font-medium ${
                activeTab === "active"
                  ? "border-b-2 border-purple-600 text-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setActiveTab("draft")}
              className={`pb-4 px-1 font-medium ${
                activeTab === "draft"
                  ? "border-b-2 border-purple-600 text-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Drafts
            </button>
            <button
              onClick={() => setActiveTab("completed")}
              className={`pb-4 px-1 font-medium ${
                activeTab === "completed"
                  ? "border-b-2 border-purple-600 text-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setActiveTab("shared")}
              className={`pb-4 px-1 font-medium ${
                activeTab === "shared"
                  ? "border-b-2 border-purple-600 text-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Shared with Me
            </button>
          </div>
        </div>

        {/* Campaigns table */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
            {error}
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {filteredCampaigns.length === 0 ? (
              <div className="p-8 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-900">
                  No campaigns found
                </h3>
                <p className="mt-1 text-gray-500">
                  Get started by creating your first campaign
                </p>
                <div className="mt-6">
                  <Link
                    to="/ai-sdr/campaign-builder"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <FaPlus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                    Create new campaign
                  </Link>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-100 border-b">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Campaign
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Leads
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCampaigns.map((campaign) => (
                      <tr
                        key={campaign.id}
                        className="hover:bg-gray-50 transition duration-150"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {campaign.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {campaign.goal || "No goal specified"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                              campaign.status
                            )}`}
                          >
                            {campaign.status || "unknown"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {campaign.leadCount || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(campaign.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-3 justify-end">
                            <button
                              onClick={() =>
                                handleViewCampaign(
                                  campaign.ownerUid || user.uid,
                                  campaign.id
                                )
                              }
                              className="text-purple-600 hover:text-purple-900"
                              title="View"
                            >
                              <FaEye />
                            </button>
                            {(campaign.isOwner ||
                              campaign.accessLevel === "edit" ||
                              campaign.accessLevel === "admin") && (
                              <Link
                                to={`/ai-sdr/campaign-builder/${campaign.id}`}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit"
                              >
                                <FaEdit />
                              </Link>
                            )}
                            {campaign.isOwner && (
                              <button
                                onClick={() =>
                                  handleDuplicateCampaign(campaign)
                                }
                                className="text-green-600 hover:text-green-900"
                                title="Duplicate"
                              >
                                <FaCopy />
                              </button>
                            )}
                            {campaign.isOwner && (
                              <button
                                onClick={() =>
                                  handleDeleteCampaign(campaign.id)
                                }
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Campaigns;
